import { GlobalPageLoadExperience } from '@atlassian/ufo';
import {
  isDatabaseContentReady,
  onDatabaseContentReady,
  setLoadingProgress,
} from '@databases/database-state/state/initial-database-loading-state';

import { getRootSkeletonAppElement } from './elements';

const START_MARK = 'mount-bootstrap-skeleton-start';
const END_MARK = 'mount-bootstrap-skeleton-end';

function forwardHtmlPerformanceMarks(): PerformanceObserver | undefined {
  const marks = performance.getEntriesByType('mark') as PerformanceMark[];
  const startMark = marks.find((m) => m.name === START_MARK);
  const endMark = marks.find((m) => m.name === END_MARK);

  if (startMark) {
    GlobalPageLoadExperience.mark(START_MARK, startMark.startTime);
  }

  if (endMark) {
    GlobalPageLoadExperience.mark(END_MARK, endMark.startTime);
    return;
  }
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === END_MARK) {
        GlobalPageLoadExperience.mark(END_MARK, entry.startTime);
        observer.disconnect();
      }
    }
  });
  observer.observe({ entryTypes: ['mark'] });

  return observer;
}

function animateProgressBar(skeletonEl: HTMLElement): () => void {
  const progressIndicator = skeletonEl.querySelector(
    '.skeleton-progress-bar-indicator'
  ) as HTMLElement | null;

  if (!progressIndicator) {
    return () => {};
  }

  const timeouts: ReturnType<typeof setTimeout>[] = [];

  const setProgress = (value: number) => {
    progressIndicator.style.width = `${value * 100}%`;
    setLoadingProgress(value);
  };

  const scheduleStep = (value: number, delay: number) => {
    const timeout = setTimeout(() => setProgress(value), delay);
    timeouts.push(timeout);
  };

  const animationFrameId = requestAnimationFrame(() => {
    setProgress(0.25);
    scheduleStep(0.5, 800);
    scheduleStep(0.9, 1600);
  });

  return () => {
    cancelAnimationFrame(animationFrameId);
    timeouts.forEach(clearTimeout);
  };
}

/**
 * Forwards performance marks set in HTML (before JS bundles load) to the UFO
 * GlobalPageLoadExperience. This must be called AFTER `startPageLoad()` so that
 * UFO captures the marks as part of the page load experience.
 */
export function forwardHtmlSkeletonMarksToUFO(): () => void {
  const observer = forwardHtmlPerformanceMarks();

  return () => {
    observer?.disconnect();
  };
}

export function mountSkeleton() {
  const skeletonEl = getRootSkeletonAppElement();

  if (skeletonEl) {
    const cleanupAnimation = animateProgressBar(skeletonEl);

    const unmount = () => {
      cleanupAnimation();
      skeletonEl.style.zIndex = '0';
      skeletonEl.style.display = 'none';
    };

    return () => {
      if (isDatabaseContentReady()) {
        unmount();
      } else {
        onDatabaseContentReady(unmount);
      }
    };
  }

  return () => null;
}
